/** @jsxImportSource preact */
import { h } from 'preact';
import { useState, useEffect, useCallback } from 'preact/hooks';
import { IncidentFilterBar, FilterDim } from './IncidentFilterBar';
import { DocumentDetailModal, DocumentDetail } from './DocumentDetailModal';
import { SECTOR_FIELDS, RISK_DOMAINS } from '@/data/chartConfigs';
import { getSubdomainInfo } from '@/data/riskTaxonomy';
import { documentUrl } from '@/lib/documentUrl';
import { documentPageUrl } from '@/lib/documentPages';
import { useModalUrl } from './useModalUrl';

interface FilterOption {
  value: string;
  label: string;
}

interface DocumentListItem {
  documentId: string;
  officialName: string;
  jurisdiction?: string;
  authority?: string;
  legislativeStatus?: string;
  activity?: string;
  activityDate?: string;
  shortSummary?: string;
}

export interface DocumentViewProps {
  apiBase: string;
  containerId: string;
  initialFilters?: Record<string, string>;
}

const MOBILE_BREAKPOINT = 640;

// Static filter options
const SUBDOMAIN_IDS = [
  '1.1','1.2','1.3','2.1','2.2','3.1','3.2','4.1','4.2','4.3',
  '5.1','5.2','6.1','6.2','6.3','6.4','6.5','6.6','7.1','7.2','7.3','7.4','7.5','7.6',
];

const RISK_SUBDOMAIN_OPTIONS: FilterOption[] = SUBDOMAIN_IDS.map((id) => ({
  value: id,
  label: `${id} - ${getSubdomainInfo(id)?.name ?? id}`,
}));

const RISK_DOMAIN_OPTIONS: FilterOption[] = RISK_DOMAINS.map((d) => ({
  value: d.id,
  label: `${d.id} - ${d.label}`,
}));

const SECTOR_OPTIONS: FilterOption[] = SECTOR_FIELDS.map((sf) => ({
  value: sf.id,
  label: sf.label,
}));

export function DocumentView({
  apiBase,
  containerId,
  initialFilters = {},
}: DocumentViewProps) {
  const [isMobile, setIsMobile] = useState(false);

  // Filter state
  const [jurisdictionFilter, setJurisdictionFilter] = useState<string[]>(
    initialFilters.jurisdiction ? [initialFilters.jurisdiction] : []
  );
  const [authorityFilter, setAuthorityFilter] = useState<string[]>(
    initialFilters.authority ? [initialFilters.authority] : []
  );
  const [legislativeStatusFilter, setLegislativeStatusFilter] = useState<string[]>(
    initialFilters.legislativeStatus ? [initialFilters.legislativeStatus] : []
  );
  const [activityFilter, setActivityFilter] = useState<string[]>(
    initialFilters.activity ? [initialFilters.activity] : []
  );
  const [appliesToFilter, setAppliesToFilter] = useState<string[]>(
    initialFilters.appliesTo ? [initialFilters.appliesTo] : []
  );
  const [lifecycleStageFilter, setLifecycleStageFilter] = useState<string[]>(
    initialFilters.lifecycleStage ? [initialFilters.lifecycleStage] : []
  );
  const [technicalScopeFilter, setTechnicalScopeFilter] = useState<string[]>(
    initialFilters.technicalScope ? [initialFilters.technicalScope] : []
  );
  const [riskSubdomainFilter, setRiskSubdomainFilter] = useState<string[]>(
    initialFilters.riskSubdomain ? [initialFilters.riskSubdomain] : []
  );
  const [riskDomainFilter, setRiskDomainFilter] = useState<string[]>(
    initialFilters.riskDomain ? [initialFilters.riskDomain] : []
  );
  const [sectorFilter, setSectorFilter] = useState<string[]>(
    initialFilters.sector ? [initialFilters.sector] : []
  );

  // Filter options from API
  const [jurisdictions, setJurisdictions] = useState<FilterOption[]>([]);
  const [authorities, setAuthorities] = useState<FilterOption[]>([]);
  const [legislativeStatuses, setLegislativeStatuses] = useState<FilterOption[]>([]);
  const [activities, setActivities] = useState<FilterOption[]>([]);
  const [appliesToOptions, setAppliesToOptions] = useState<FilterOption[]>([]);
  const [lifecycleStageOptions, setLifecycleStageOptions] = useState<FilterOption[]>([]);
  const [technicalScopeOptions, setTechnicalScopeOptions] = useState<FilterOption[]>([]);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Sort state
  type SortField = 'documentId' | 'officialName' | 'jurisdiction' | 'authority' | 'legislativeStatus' | 'activity' | 'activityDate';
  const [sortBy, setSortBy] = useState<SortField>('documentId');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Document list state
  const [documents, setDocuments] = useState<DocumentListItem[]>([]);
  const [totalDocuments, setTotalDocuments] = useState<number | null>(null);
  const [listLoading, setListLoading] = useState(true);

  // Document detail modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailDocumentId, setDetailDocumentId] = useState('');
  const [detailDocument, setDetailDocument] = useState<DocumentDetail | null>(null);
  const [detailModalLoading, setDetailModalLoading] = useState(false);

  // Detect mobile
  useEffect(() => {
    const updateMobile = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    updateMobile();
    window.addEventListener('resize', updateMobile);
    return () => window.removeEventListener('resize', updateMobile);
  }, []);

  // Load filter options + total document count once on mount
  useEffect(() => {
    fetch(`${apiBase}/data/risk-subdomain?stackBy=coverage`)
      .then((r) => r.json())
      .then((json) => {
        if (json.totalDocumentsAll != null) setTotalDocuments(json.totalDocumentsAll);
        if (json.jurisdictions) setJurisdictions(json.jurisdictions.map((v: string) => ({ value: v, label: v })));
        if (json.authorities) setAuthorities(json.authorities.map((v: string) => ({ value: v, label: v })));
        if (json.legislativeStatuses) setLegislativeStatuses(json.legislativeStatuses.map((v: string) => ({ value: v, label: v })));
        if (json.activities) setActivities(json.activities.map((v: string) => ({ value: v, label: v })));
        if (json.appliesToOptions) setAppliesToOptions(json.appliesToOptions.map((v: string) => ({ value: v, label: v })));
        if (json.lifecycleStageOptions) setLifecycleStageOptions(json.lifecycleStageOptions.map((v: string) => ({ value: v, label: v })));
        if (json.technicalScopeOptions) setTechnicalScopeOptions(json.technicalScopeOptions.map((v: string) => ({ value: v, label: v })));
      })
      .catch((err) => console.error('Failed to fetch filter options:', err));
  }, [apiBase]);

  // Fetch document list whenever filters change
  useEffect(() => {
    let cancelled = false;
    setListLoading(true);

    const qs = new URLSearchParams();
    qs.set('filterType', 'all');
    if (jurisdictionFilter.length) qs.set('jurisdiction', jurisdictionFilter.join(','));
    if (authorityFilter.length) qs.set('authority', authorityFilter.join(','));
    if (legislativeStatusFilter.length) qs.set('legislativeStatus', legislativeStatusFilter.join(','));
    if (activityFilter.length) qs.set('activity', activityFilter.join(','));
    if (appliesToFilter.length) qs.set('appliesTo', appliesToFilter.join(','));
    if (lifecycleStageFilter.length) qs.set('lifecycleStage', lifecycleStageFilter.join(','));
    if (technicalScopeFilter.length) qs.set('technicalScope', technicalScopeFilter.join(','));
    if (riskSubdomainFilter.length) qs.set('riskSubdomain', riskSubdomainFilter.join(','));
    if (riskDomainFilter.length) qs.set('riskDomain', riskDomainFilter.join(','));
    if (sectorFilter.length) qs.set('sector', sectorFilter.join(','));

    fetch(`${apiBase}/data/risk-subdomain/details?${qs}`)
      .then((r) => r.json())
      .then((json: { items?: DocumentListItem[] }) => {
        if (!cancelled) setDocuments(json?.items ?? []);
      })
      .catch((err) => console.error('Failed to fetch documents:', err))
      .finally(() => { if (!cancelled) setListLoading(false); });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, jurisdictionFilter.join(','), authorityFilter.join(','), legislativeStatusFilter.join(','), activityFilter.join(','), appliesToFilter.join(','), lifecycleStageFilter.join(','), technicalScopeFilter.join(','), riskSubdomainFilter.join(','), riskDomainFilter.join(','), sectorFilter.join(',')]);

  const openDetail = useCallback((documentId: string) => {
    setDetailDocumentId(documentId);
    setDetailModalOpen(true);
    setDetailModalLoading(true);
    setDetailDocument(null);

    fetch(`${apiBase}/data/risk-subdomain/document?documentId=${encodeURIComponent(documentId)}`)
      .then((r) => r.json())
      .then((json: { document?: DocumentDetail }) => {
        setDetailDocument(json?.document ?? null);
      })
      .catch((err) => console.error('Failed to fetch document details:', err))
      .finally(() => setDetailModalLoading(false));
  }, [apiBase]);

  // The open modal is mirrored in the URL (?document=<id>) so it can be
  // shared and Back closes it. See lib/modalUrl.
  const closeDetail = useCallback(() => setDetailModalOpen(false), []);
  const docUrl = useModalUrl(documentUrl, containerId, openDetail, closeDetail);

  const handleDocumentClick = useCallback((documentId: string) => {
    openDetail(documentId);
    docUrl.push(documentId);
  }, [openDetail, docUrl.push]);

  const handleDetailClose = useCallback(() => {
    closeDetail();
    docUrl.clear();
  }, [closeDetail, docUrl.clear]);

  const handleReset = () => {
    setJurisdictionFilter([]);
    setAuthorityFilter([]);
    setLegislativeStatusFilter([]);
    setActivityFilter([]);
    setAppliesToFilter([]);
    setLifecycleStageFilter([]);
    setTechnicalScopeFilter([]);
    setRiskSubdomainFilter([]);
    setRiskDomainFilter([]);
    setSectorFilter([]);
    setSearchQuery('');
  };

  const hasActiveFilters = [
    jurisdictionFilter, authorityFilter, legislativeStatusFilter,
    activityFilter, appliesToFilter, lifecycleStageFilter,
    technicalScopeFilter, riskSubdomainFilter, riskDomainFilter, sectorFilter,
  ].some((f) => f.length > 0) || searchQuery.length > 0;

  // Client-side search filtering
  const searchedDocuments = searchQuery.trim()
    ? documents.filter((doc) => {
        const q = searchQuery.toLowerCase();
        return (
          (doc.documentId && doc.documentId.toLowerCase().includes(q)) ||
          (doc.officialName && doc.officialName.toLowerCase().includes(q)) ||
          (doc.jurisdiction && doc.jurisdiction.toLowerCase().includes(q)) ||
          (doc.authority && doc.authority.toLowerCase().includes(q)) ||
          (doc.legislativeStatus && doc.legislativeStatus.toLowerCase().includes(q)) ||
          (doc.activity && doc.activity.toLowerCase().includes(q)) ||
          (doc.activityDate && doc.activityDate.toLowerCase().includes(q))
        );
      })
    : documents;

  // Numeric sort helper for document IDs (e.g. "123" < "1000")
  const parseNumericId = (id: string): number => {
    const n = parseInt(id, 10);
    return isNaN(n) ? Infinity : n;
  };

  // Client-side sorting
  const filteredDocuments = [...searchedDocuments].sort((a, b) => {
    let cmp: number;
    if (sortBy === 'documentId') {
      cmp = parseNumericId(a.documentId) - parseNumericId(b.documentId);
    } else {
      const aVal = (a[sortBy] ?? '').toLowerCase();
      const bVal = (b[sortBy] ?? '').toLowerCase();
      cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const sortIndicator = (field: SortField) =>
    sortBy === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  // Build dim list for IncidentFilterBar. Each entry becomes either an
  // inactive option in the Add-filter menu or a chip on the bar.
  const filterDims: FilterDim[] = [
    { key: 'jurisdiction',      label: 'Jurisdiction',       options: jurisdictions,            selected: jurisdictionFilter,      onChange: setJurisdictionFilter },
    { key: 'authority',         label: 'Authority',          options: authorities,              selected: authorityFilter,         onChange: setAuthorityFilter },
    { key: 'legislativeStatus', label: 'Legislative status', options: legislativeStatuses,      selected: legislativeStatusFilter, onChange: setLegislativeStatusFilter },
    { key: 'activity',          label: 'Most recent activity', options: activities,             selected: activityFilter,          onChange: setActivityFilter },
    { key: 'appliesTo',         label: 'Primarily applies to', options: appliesToOptions,       selected: appliesToFilter,         onChange: setAppliesToFilter },
    { key: 'lifecycleStage',    label: 'Lifecycle stage',    options: lifecycleStageOptions,    selected: lifecycleStageFilter,    onChange: setLifecycleStageFilter },
    { key: 'technicalScope',    label: 'Technical scope',    options: technicalScopeOptions,    selected: technicalScopeFilter,    onChange: setTechnicalScopeFilter },
    { key: 'riskDomain',        label: 'Risk domain',        options: RISK_DOMAIN_OPTIONS,      selected: riskDomainFilter,        onChange: setRiskDomainFilter,    tooltip: 'Filter for documents with Good Coverage on any subdomain' },
    { key: 'riskSubdomain',     label: 'Risk subdomain',     options: RISK_SUBDOMAIN_OPTIONS,   selected: riskSubdomainFilter,     onChange: setRiskSubdomainFilter, tooltip: 'Filter for documents with Good Coverage' },
    { key: 'sector',            label: 'Sector',             options: SECTOR_OPTIONS,           selected: sectorFilter,            onChange: setSectorFilter,        tooltip: 'Filter for documents with Good Coverage' },
  ];

  return (
    <div style={{ position: 'relative', background: 'transparent', color: 'inherit' }}>
      {/* Filter bar */}
      <IncidentFilterBar
        availableStackBy={[]}
        stackBy={null}
        onStackByChange={() => { /* no stack-by on the document list view */ }}
        stackByLabels={{}}
        dimensions={filterDims}
        severityFields={[]}
        severityFilters={{}}
        onSeverityChange={() => { /* no severity dimension on document list */ }}
        hasActiveFilter={hasActiveFilters}
        onResetAll={handleReset}
        isMobile={isMobile}
      />

      {/* Search bar + document count */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        marginBottom: 8,
      }}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <input
            type="text"
            value={searchQuery}
            onInput={(e) => setSearchQuery((e.target as HTMLInputElement).value)}
            placeholder="Search documents..."
            style={{
              padding: '4px 8px',
              paddingRight: searchQuery ? 24 : 8,
              fontSize: 11,
              border: '1px solid var(--gc-control-border, currentColor)',
              borderRadius: 9,
              background: 'transparent',
              color: 'inherit',
              width: isMobile ? 160 : 200,
              outline: 'none',
              opacity: 0.8,
            }}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute',
                right: 6,
                top: '50%',
                transform: 'translateY(-50%)',
                border: 'none',
                background: 'none',
                color: 'var(--gc-text-muted, #6b7280)',
                cursor: 'pointer',
                fontSize: 12,
                padding: 0,
                lineHeight: 1,
              }}
              title="Clear search"
            >
              ×
            </button>
          )}
        </div>
        <div style={{
          fontSize: 12,
          color: 'var(--gc-text-muted, #6b7280)',
          whiteSpace: 'nowrap',
        }}>
          {listLoading ? 'Loading...' : (
            searchQuery.trim()
              ? `${filteredDocuments.length} of ${documents.length} documents${totalDocuments != null ? ` (${totalDocuments} total)` : ''}`
              : `Showing ${documents.length}${totalDocuments != null && documents.length !== totalDocuments ? ` of ${totalDocuments}` : ''} documents`
          )}
        </div>
      </div>

      {/* Document list table */}
      <div style={{
        border: '1px solid var(--gc-border, #e5e7eb)',
        borderRadius: 6,
        overflow: 'hidden',
      }}>
        <div style={{ maxHeight: 500, overflowY: 'auto' }}>
          {/* Table header (sticky) */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '60px 1fr' : '50px 1fr 100px 120px 80px 80px 90px',
            gap: 0,
            padding: '8px 12px',
            background: 'var(--gc-surface-muted, #f1f5f9)',
            borderBottom: '1px solid var(--gc-border, #e5e7eb)',
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--gc-text-muted, #6b7280)',
            userSelect: 'none',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}>
            <div onClick={() => handleSort('documentId')} style={{ cursor: 'pointer', textAlign: 'left', justifySelf: 'start' }}>
              ID{sortIndicator('documentId')}
            </div>
            <div onClick={() => handleSort('officialName')} style={{ cursor: 'pointer', textAlign: 'left', justifySelf: 'start' }}>
              Name{sortIndicator('officialName')}
            </div>
            {!isMobile && (
              <div onClick={() => handleSort('jurisdiction')} style={{ cursor: 'pointer', textAlign: 'left', justifySelf: 'start' }}>
                Jurisdiction{sortIndicator('jurisdiction')}
              </div>
            )}
            {!isMobile && (
              <div onClick={() => handleSort('authority')} style={{ cursor: 'pointer', textAlign: 'left', justifySelf: 'start' }}>
                Authority{sortIndicator('authority')}
              </div>
            )}
            {!isMobile && (
              <div onClick={() => handleSort('legislativeStatus')} style={{ cursor: 'pointer', textAlign: 'left', justifySelf: 'start' }}>
                Status{sortIndicator('legislativeStatus')}
              </div>
            )}
            {!isMobile && (
              <div onClick={() => handleSort('activity')} style={{ cursor: 'pointer', textAlign: 'left', justifySelf: 'start' }}>
                Most Recent Activity{sortIndicator('activity')}
              </div>
            )}
            {!isMobile && (
              <div onClick={() => handleSort('activityDate')} style={{ cursor: 'pointer', textAlign: 'left', justifySelf: 'start' }}>
                Activity Date{sortIndicator('activityDate')}
              </div>
            )}
          </div>

          {/* Table body */}
          {listLoading ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--gc-text-muted, #6b7280)', fontSize: 13 }}>
              Loading...
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--gc-text-muted, #6b7280)', fontSize: 13 }}>
              No documents found matching the current filters.
            </div>
          ) : (
            filteredDocuments.map((doc) => {
              // A real link to the document's page when the pages are live, so
              // crawlers (and a middle-click) reach it; a plain click still
              // opens the modal.
              const href = documentPageUrl(doc.documentId, doc.officialName || '');
              const Row = href ? 'a' : 'div';
              return (
              <Row
                key={doc.documentId}
                href={href || undefined}
                onClick={(e: MouseEvent) => {
                  if (href && (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) return;
                  e.preventDefault();
                  handleDocumentClick(doc.documentId);
                }}
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '60px 1fr' : '50px 1fr 100px 120px 80px 80px 90px',
                  alignItems: 'center',
                  gap: 0,
                  padding: '6px 12px',
                  borderBottom: '1px solid var(--gc-border, #e5e7eb)',
                  fontSize: 12,
                  cursor: 'pointer',
                  color: 'inherit',
                  textDecoration: 'none',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'var(--gc-surface-muted, #f1f5f9)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                }}
              >
                <div style={{ color: 'var(--gc-link, inherit)', fontWeight: 500 }}>
                  {doc.documentId}
                </div>
                <div
                  title={doc.shortSummary || undefined}
                  style={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: '1.4',
                    paddingRight: 8,
                  }}
                >
                  {doc.officialName}
                </div>
                {!isMobile && (
                  <div style={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: '1.4',
                    color: 'var(--gc-text-muted, #6b7280)',
                  }}>
                    {doc.jurisdiction || '—'}
                  </div>
                )}
                {!isMobile && (
                  <div style={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: '1.4',
                    color: 'var(--gc-text-muted, #6b7280)',
                  }}>
                    {doc.authority || '—'}
                  </div>
                )}
                {!isMobile && (
                  <div style={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: '1.4',
                    color: 'var(--gc-text-muted, #6b7280)',
                  }}>
                    {doc.legislativeStatus || '—'}
                  </div>
                )}
                {!isMobile && (
                  <div style={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: '1.4',
                    color: 'var(--gc-text-muted, #6b7280)',
                  }}>
                    {doc.activity || '—'}
                  </div>
                )}
                {!isMobile && (
                  <div style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: 'var(--gc-text-muted, #6b7280)',
                    fontSize: 11,
                  }}>
                    {doc.activityDate || '—'}
                  </div>
                )}
              </Row>
              );
            })
          )}
        </div>
      </div>

      <DocumentDetailModal
        isOpen={detailModalOpen}
        onClose={handleDetailClose}
        documentId={detailDocumentId}
        document={detailDocument}
        loading={detailModalLoading}
      />
    </div>
  );
}
